import React, { useEffect, useRef, useState } from "react";
import { getWorkTypeStatuses, moveWork } from "../../../../api/hub/work";
import {
  popupBg,
  popupBorder,
  popupText,
  popupSubtext,
  popupBtn,
} from "../constants";

// <MoveWorkMenu> — the "Move work" status-choice control (D4 / §A-1).
//
// `fn_work_move(p_task_id, p_status)` sets a task's status, but a status is a
// deliberate choice — `needs_assistance` refuses `fn_now_dismiss` precisely so a
// person decides whether the work is still in progress or actually done. So the
// button stays "Move work" and the choice is the point.
//
// The status vocabulary is `ops_work_types.statuses` — a jsonb array PER WORK
// TYPE (nine work types, each its own list), read via getWorkTypeStatuses and
// cached for the session. Only statuses whose `type` is not `blocked` are
// offered (you don't move work INTO a blocked state), defaulting to in_progress.
// Never hardcoded (KB §16).
//
// The server validates the status against the task's own pipeline and raises
// with the valid list if it is wrong — that message is shown inline verbatim
// (KB §18: a wrong value is a backend bug to report, not patch).
//
// Reused by <WorkPanel> (the task detail) and <NowRow> (the needs_assistance row).
export default function MoveWorkMenu({
  workType,
  taskId,
  buttonLabel = "Move work",
  variant = "primary",
  disabled = false,
  onMoved,
  className = "",
  buttonClassName,
  popoverClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const popRef = useRef(null);

  // Load this work type's status list when the menu opens (lazy — no fetch on
  // mount, so rendering a row that carries the menu never touches the network).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getWorkTypeStatuses(workType)
      .then((list) => {
        if (cancelled) return;
        const movable = (list || []).filter((s) => s && s.type !== "blocked");
        setStatuses(movable);
        const def = movable.find((s) => s.key === "in_progress") || movable[0] || null;
        setSelected(def ? def.key : null);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setStatuses([]);
        setLoading(false);
        setError(e?.message || "Couldn't load statuses.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, workType]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const confirm = async () => {
    if (!selected || pending) return;
    setPending(true);
    setError(null);
    try {
      await moveWork(taskId, selected);
      setOpen(false);
      onMoved?.(selected);
    } catch (e) {
      setError(e?.message || "Couldn't move the work.");
    } finally {
      setPending(false);
    }
  };

  // A caller can restyle the trigger (the Now row passes its primary button style); the
  // default look is unchanged.
  const btnClass =
    buttonClassName ??
    (variant === "primary"
      ? "rounded-lg bg-[#32B1FF] px-3 py-1.5 text-sm font-medium text-white"
      : "rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-200");

  return (
    <div className={`relative ${className}`} ref={popRef}>
      <button
        type="button"
        data-action="moveWork"
        data-testid="move-work-menu"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={btnClass}
      >
        {buttonLabel}
      </button>
      {open && (
        <div
          className={`absolute z-20 mt-1 w-56 rounded-lg shadow-lg ${popupBg} ${popupBorder} ${popupText} p-2${popoverClassName ? ` ${popoverClassName}` : ""}`}
          role="menu"
          data-testid="move-work-popover"
        >
          {loading && (
            <p className={`px-2 py-2 text-sm ${popupSubtext}`}>Loading statuses…</p>
          )}
          {!loading && statuses.length === 0 && !error && (
            <p className={`px-2 py-2 text-sm ${popupSubtext}`}>
              No movable statuses for this work type.
            </p>
          )}
          {!loading && statuses.length > 0 && (
            <ul className="space-y-0.5">
              {statuses.map((s) => (
                <li key={s.key}>
                  <button
                    type="button"
                    data-status={s.key}
                    onClick={() => setSelected(s.key)}
                    className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm ${popupBtn} ${
                      selected === s.key
                        ? "ring-1 ring-[#32B1FF] text-gray-900 dark:text-white"
                        : ""
                    }`}
                  >
                    <span>{s.label || s.key}</span>
                    {selected === s.key && (
                      <span className="text-[#32B1FF]" aria-hidden="true">✓</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {error && (
            <p
              className="mt-1 px-2 py-1.5 text-xs text-red-600 dark:text-red-400"
              data-testid="move-work-error"
            >
              {error}
            </p>
          )}
          <div className="mt-2 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className={`rounded-md px-2.5 py-1 text-sm ${popupBtn}`}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="move-work-confirm"
              disabled={!selected || pending}
              onClick={confirm}
              className="rounded-md bg-[#32B1FF] px-2.5 py-1 text-sm font-medium text-white disabled:opacity-50"
            >
              {pending ? "Moving…" : "Move"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

